/**
 * Campaign Performance Reporting Dashboard
 * Comprehensive analytics and reporting interface for campaign performance
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    ChartBarIcon, 
    ArrowTrendingUpIcon, 
    ArrowTrendingDownIcon,
    CalendarIcon,
    ArrowDownTrayIcon,
    FunnelIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import CampaignTimeSeriesChart from './CampaignTimeSeriesChart';

const CampaignReportingDashboard = () => {
    const { token } = useAuth();
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedTimeRange, setSelectedTimeRange] = useState('30');
    const [selectedCampaignType, setSelectedCampaignType] = useState('all');
    const [activeTab, setActiveTab] = useState('overview');
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        campaignTypes: [],
        includeRefunds: true
    });

    const timeRangeOptions = [
        { value: '7', label: 'Son 7 Gün' },
        { value: '30', label: 'Son 30 Gün' },
        { value: '90', label: 'Son 90 Gün' },
        { value: 'custom', label: 'Özel Tarih' }
    ];

    const campaignTypeOptions = [
        { value: 'all', label: 'Tüm Türler' },
        { value: 'flash_sale', label: 'Flash Sale' },
        { value: 'category', label: 'Kategori İndirimi' },
        { value: 'cart', label: 'Sepet Kampanyası' },
        { value: 'coupon', label: 'Kupon İndirimi' },
        { value: 'package', label: 'Paket Kampanyası' },
        { value: 'special_day', label: 'Özel Gün' }
    ];

    // Load report data
    const loadReportData = useCallback(async () => {
        try {
            setLoading(true);
            
            let endpoint = '/api/campaign-reports/performance';
            const params = new URLSearchParams();
            
            if (selectedTimeRange !== 'custom') {
                const endDate = new Date().toISOString();
                const startDate = new Date();
                startDate.setDate(startDate.getDate() - parseInt(selectedTimeRange));
                
                params.append('startDate', startDate.toISOString());
                params.append('endDate', endDate);
            } else if (filters.startDate && filters.endDate) {
                params.append('startDate', filters.startDate);
                params.append('endDate', filters.endDate);
            }
            
            if (selectedCampaignType !== 'all') {
                params.append('campaignTypes', selectedCampaignType);
            }
            
            params.append('includeRefunds', 'true');

            const response = await api.get(`${endpoint}?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('📊 API Response:', response.data);

            if (response.data && response.data.success) {
                // Normal format: { success: true, data: {...} }
                setReportData(response.data.data);
            } else if (response.data && response.data.campaigns) {
                // Direkt format: { campaigns: [...], summary: {...}, daily_trends: [...] }
                setReportData(response.data);
            } else if (response.data && response.data.data) {
                // Fallback: Eğer success field'i yoksa ama data varsa
                setReportData(response.data.data);
            } else {
                console.error('❌ API Response format hatası:', response.data);
                throw new Error('Report generation failed');
            }

        } catch (error) {
            console.error('Report loading error:', error);
            
            // Hata türüne göre özel mesajlar
            if (error.message.includes('Failed to fetch')) {
                toast.error('Sunucu bağlantısı kurulamadı. Lütfen internet bağlantınızı kontrol edin.');
            } else if (error.message.includes('CORS')) {
                toast.error('Güvenlik hatası. Sayfa yenilenerek tekrar denenecek.');
                setTimeout(() => window.location.reload(), 2000);
            } else if (error.status === 404) {
                toast.error('Kampanya raporları servisi bulunamadı. Lütfen sistem yöneticisine başvurun.');
            } else if (error.status === 500) {
                toast.error('Sunucu hatası. Lütfen daha sonra tekrar deneyin.');
            } else {
                toast.error('Rapor yüklenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
            }
        } finally {
            setLoading(false);
        }
    }, [selectedTimeRange, selectedCampaignType, filters, token]);

    useEffect(() => {
        loadReportData();
    }, [loadReportData]);

    // Export report
    const exportReport = async (format = 'csv') => {
        try {
            const params = new URLSearchParams();
            
            if (selectedTimeRange !== 'custom') {
                const endDate = new Date().toISOString();
                const startDate = new Date();
                startDate.setDate(startDate.getDate() - parseInt(selectedTimeRange));
                
                params.append('startDate', startDate.toISOString());
                params.append('endDate', endDate);
            }
            
            if (selectedCampaignType !== 'all') {
                params.append('campaignTypes', selectedCampaignType);
            }
            
            params.append('format', format);

            const response = await api.get(`/api/campaign-reports/performance?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: format === 'csv' ? 'blob' : 'json'
            });

            if (format === 'csv') {
                const blob = new Blob([response.data], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `campaign-performance-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                toast.success('Rapor CSV olarak indirildi');
            }

        } catch (error) {
            console.error('Export error:', error);
            toast.error('Rapor dışa aktarılırken hata oluştu');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="animate-pulse space-y-6">
                        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="h-24 bg-gray-200 rounded"></div>
                            ))}
                        </div>
                        <div className="h-64 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!reportData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Rapor verileri yüklenemedi</p>
                    <button 
                        onClick={loadReportData}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Tekrar Dene
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            📊 Kampanya Performans Raporu
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Kampanya performansını analiz edin ve insights keşfedin
                        </p>
                    </div>
                    
                    <div className="flex items-center space-x-4 mt-4 lg:mt-0">
                        <button
                            onClick={() => exportReport('csv')}
                            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>CSV İndir</span>
                        </button>
                        
                        <button
                            onClick={loadReportData}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            <span>Yenile</span>
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <FunnelIcon className="h-5 w-5 mr-2" />
                        Filtreler
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Zaman Aralığı
                            </label>
                            <select
                                value={selectedTimeRange}
                                onChange={(e) => setSelectedTimeRange(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {timeRangeOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Kampanya Türü
                            </label>
                            <select
                                value={selectedCampaignType}
                                onChange={(e) => setSelectedCampaignType(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {campaignTypeOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        {selectedTimeRange === 'custom' && (
                            <div className="flex space-x-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Başlangıç
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.startDate.split('T')[0]}
                                        onChange={(e) => setFilters(prev => ({
                                            ...prev,
                                            startDate: e.target.value + 'T00:00:00.000Z'
                                        }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Bitiş
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.endDate.split('T')[0]}
                                        onChange={(e) => setFilters(prev => ({
                                            ...prev,
                                            endDate: e.target.value + 'T23:59:59.999Z'
                                        }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-blue-100 text-sm font-medium">Toplam Kullanım</p>
                                <p className="text-3xl font-bold">{(reportData.summary.totalCampaignUsage || reportData.summary.total_usage_count || 0).toLocaleString()}</p>
                            </div>
                            <ChartBarIcon className="h-8 w-8 text-blue-200" />
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-green-100 text-sm font-medium">Toplam İndirim</p>
                                <p className="text-3xl font-bold">₺{(reportData.summary.totalDiscountGiven || reportData.summary.total_discount_given || 0).toLocaleString()}</p>
                            </div>
                            <ArrowTrendingUpIcon className="h-8 w-8 text-green-200" />
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-purple-100 text-sm font-medium">Benzersiz Kullanıcı</p>
                                <p className="text-3xl font-bold">{(reportData.summary.uniqueUsers || reportData.summary.unique_users || 0).toLocaleString()}</p>
                            </div>
                            <div className="h-8 w-8 text-purple-200">👥</div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className={`bg-gradient-to-r rounded-xl p-6 text-white ${
                            (reportData.summary.refundRate || reportData.summary.refund_rate || 0) < 10 
                                ? 'from-emerald-500 to-emerald-600' 
                                : 'from-orange-500 to-orange-600'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-emerald-100 text-sm font-medium">İade Oranı</p>
                                <p className="text-3xl font-bold">%{(reportData.summary.refundRate || reportData.summary.refund_rate || 0).toFixed(1)}</p>
                            </div>
                            {(reportData.summary.refundRate || reportData.summary.refund_rate || 0) < 10 ? (
                                <ArrowTrendingUpIcon className="h-8 w-8 text-emerald-200" />
                            ) : (
                                <ArrowTrendingDownIcon className="h-8 w-8 text-orange-200" />
                            )}
                        </div>
                    </motion.div>
                </div>

                {/* Insights */}
                {reportData.insights && reportData.insights.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            💡 Önemli Insights
                        </h2>
                        
                        <div className="space-y-4">
                            {reportData.insights.map((insight, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className={`flex items-start space-x-3 p-4 rounded-lg ${
                                        insight.type === 'success' ? 'bg-green-50 border border-green-200' :
                                        insight.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                                        'bg-blue-50 border border-blue-200'
                                    }`}
                                >
                                    <div className="flex-shrink-0">
                                        {insight.type === 'success' && (
                                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                        )}
                                        {insight.type === 'warning' && (
                                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                                        )}
                                        {insight.type === 'info' && (
                                            <InformationCircleIcon className="h-5 w-5 text-blue-500" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900">{insight.title}</h3>
                                        <p className="text-gray-600 text-sm">{insight.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Performance Tables */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top Campaigns */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            🏆 En Başarılı Kampanyalar
                        </h2>
                        
                        <div className="space-y-3">
                            {reportData.campaignPerformance && reportData.campaignPerformance.slice(0, 5).map((campaign, index) => (
                                <div key={campaign.campaignId || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <span className="text-blue-600 font-semibold text-sm">{index + 1}</span>
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900">{campaign.campaignName || campaign.campaign_name || 'Bilinmeyen Kampanya'}</p>
                                            <p className="text-sm text-gray-500">{(campaign.usageCount || campaign.usage_count || 0)} kullanım</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold text-gray-900">₺{(campaign.totalDiscountGiven || campaign.total_discount_given || 0).toFixed(2)}</p>
                                        <p className="text-sm text-gray-500">%{(campaign.refundRate || campaign.refund_rate || 0).toFixed(1)} iade</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Campaign Types Performance */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            📊 Türlere Göre Performans
                        </h2>
                        
                        <div className="space-y-3">
                            {reportData.typePerformance && reportData.typePerformance.map((type, index) => (
                                <div key={type.campaignType || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div>
                                        <p className="font-medium text-gray-900">
                                            {getCampaignTypeDisplayName(type.campaignType || type.campaign_type)}
                                        </p>
                                        <p className="text-sm text-gray-500">{(type.usageCount || type.usage_count || 0)} kullanım</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold text-gray-900">₺{(type.totalDiscountGiven || type.total_discount_given || 0).toFixed(2)}</p>
                                        <p className="text-sm text-gray-500">{(type.uniqueUsers || type.unique_users || 0)} kullanıcı</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Time Series Chart */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        📈 Zaman Serisi Analizi
                    </h2>
                    
                    <CampaignTimeSeriesChart data={reportData} />
                </div>
            </div>
        </div>
    );
};

// Helper function for campaign type display names
const getCampaignTypeDisplayName = (type) => {
    const displayNames = {
        'flash_sale': 'Flash Sale',
        'category': 'Kategori İndirimi',
        'cart': 'Sepet Kampanyası',
        'coupon': 'Kupon İndirimi',
        'package': 'Paket Kampanyası',
        'special_day': 'Özel Gün Kampanyası'
    };
    return displayNames[type] || type;
};

export default CampaignReportingDashboard;